import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { getCachedTierInfo } from '../services/tierService';
import { useNavState } from '../contexts/NavContext';
import './Navbar.css';

function Navbar() {
  const { instance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const { navCollapsed, toggleNav } = useNavState();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isTrial, setIsTrial] = useState(false);

  // Fetch trial status
  useEffect(() => {
    getCachedTierInfo().then(info => {
      if (info?.tier === 'trial' && !info.isTrialExpired) {
        setIsTrial(true);
        setTrialDaysLeft(info.daysRemaining || 0);
      } else {
        setIsTrial(false);
        setTrialDaysLeft(null);
      }
    }).catch(() => {});
  }, []);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setOpenDropdown(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change (handles browser back/forward)
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleLogout = (e) => {
    e.preventDefault();
    if (isDevelopment) {
      console.log('Logout clicked - navigating to home');
      navigate('/');
    } else {
      instance.logoutRedirect();
    }
  };

  const isActive = (path) => location.pathname === path;

  const isDropdownActive = useCallback((items) => {
    return items.some(item => location.pathname === item.path);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setOpenDropdown(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownClick = (dropdownId) => {
    if (isMobile) {
      setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
    }
  };

  // Navigation structure with dropdowns
  const reconScansItems = [
    { label: 'Fast Scan', path: '/fast-scan', icon: '⚡' },
    { label: 'Deep Scan', path: '/comprehensive-scan', icon: '🔬' },
    { label: 'Tools', path: '/tools', icon: '🛠️' }
  ];

  const continuousMonitoringItems = [
    { label: 'Command Center', path: '/command-center', icon: '📊' },
    { label: 'Assets', path: '/assets', icon: '🌐' },
    { label: 'Drift Alarms', path: '/drift-alarms', icon: '🔔' },
    { label: 'Drift Analytics', path: '/drift-analytics', icon: '📈' },
    { label: 'Drift Rules', path: '/drift-rules', icon: '⚙️' },
    { label: 'Inventory', path: '/inventory', icon: '📦' },
    { label: 'Suppressions', path: '/suppressions', icon: '🔇' },
    { label: 'Schedules', path: '/schedules', icon: '📅' },
    { label: 'Validate', path: '/validate', icon: '✓' }
  ];

  const aboutItems = [
    { label: 'Pricing', path: '/upgrade', icon: '💰' },
    { label: 'How It Works', path: '/how-it-works', icon: '📖' }
  ];

  // Dropdown component
  const NavDropdown = ({ id, label, icon, items }) => {
    const hasActiveChild = isDropdownActive(items);
    const isOpen = openDropdown === id;

    return (
      <li
        className={`nav-dropdown ${hasActiveChild ? 'has-active' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => handleDropdownClick(id)}
      >
        <button className="nav-dropdown-trigger">
          <span className="nav-icon">{icon}</span>
          {label}
          <span className="dropdown-arrow">▼</span>
        </button>
        <ul className="dropdown-menu">
          {items.map(item => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <span className="dropdown-icon">{item.icon}</span>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <nav className={`navbar ${navCollapsed ? 'nav-closed' : ''}`}>
      {/* Brand Zone — hamburger + alarm logo + DriftAlarm wordmark.
          Width matches the left-nav so the divider lines up. */}
      <div className="brand-zone">
        <button
          className="leftnav-toggle"
          onClick={toggleNav}
          aria-label={navCollapsed ? 'Show navigation' : 'Hide navigation'}
          title={navCollapsed ? 'Show navigation' : 'Hide navigation'}
        >
          <span className="leftnav-toggle-lines">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <Link to="/command-center" className="navbar-brand" onClick={closeMobileMenu}>
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="18" r="11" stroke="var(--neon-blue, #00a8ff)" strokeWidth="2.2"/>
              <path d="M5 8 L9 4 M27 8 L23 4" stroke="var(--neon-blue, #00a8ff)" strokeWidth="2.2" strokeLinecap="round"/>
              <path d="M11 18 L14.5 21.5 L21 14" stroke="var(--accent-cyan, #00d4aa)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className="brand-text">DriftAlarm</span>
        </Link>
      </div>

      {/* Mobile Menu Toggle */}
      <button className="mobile-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
        <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="nav-mobile-overlay" onClick={closeMobileMenu} />
      )}

      {/* Navigation Menu */}
      <ul className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Recon Scans Dropdown */}
        <NavDropdown
          id="recon-scans"
          label="Recon Scans"
          icon="🔍"
          items={reconScansItems}
        />

        {/* Continuous Monitoring Dropdown */}
        <NavDropdown
          id="continuous-monitoring"
          label="Monitoring"
          icon="📡"
          items={continuousMonitoringItems}
        />

        {/* Reports */}
        <li>
          <Link
            to="/reports"
            className={`nav-link ${isActive('/reports') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span className="nav-icon">📋</span>
            Reports
          </Link>
        </li>

        {/* About Dropdown */}
        <NavDropdown
          id="about"
          label="About"
          icon="ℹ️"
          items={aboutItems}
        />

        {/* Trial Badge */}
        {isTrial && (
          <li className="nav-trial-badge-item">
            <Link to="/upgrade" className="nav-trial-badge" onClick={closeMobileMenu}>
              {isMobile
                ? `${trialDaysLeft}d left`
                : trialDaysLeft <= 1
                  ? 'Trial: Expires today!'
                  : `Trial: ${trialDaysLeft} days left`
              }
            </Link>
          </li>
        )}

        {/* Divider */}
        <li className="nav-divider"></li>

        {/* Settings Icon */}
        <li>
          <Link
            to="/settings"
            className={`settings-link ${isActive('/settings') ? 'active' : ''}`}
            onClick={closeMobileMenu}
            title="Settings"
          >
            <span className="settings-icon">⚙️</span>
            <span className="settings-text">Settings</span>
          </Link>
        </li>

        {/* Logout Button */}
        <li>
          <button onClick={handleLogout} className="logout-button">
            <span className="logout-icon">⏻</span>
            Logout
          </button>
        </li>
      </ul>

      {/* Scanline Effect */}
      <div className="navbar-scanline"></div>
    </nav>
  );
}

export default Navbar;
